import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchManifest } from '@/services/assessment/query/fetchManifest';
import {
  Manifest,
  ScheduleAssessmentPayload,
} from '@/features/schedule_assessments/models/Students';
import { batchAddAssignment } from '@/services/assessment/query/batchAddAssessments';

interface ManifestResponse {
  generateManifest: {
    activities: Manifest[];
  };
}

interface ScheduleAssessmentState {
  loading: number;
  manifestActivities: { [key: number]: Manifest[] };
  error: string | null;
  failedStudentIds: string[];
}

const initialState: ScheduleAssessmentState = {
  loading: 0,
  manifestActivities: {},
  error: null,
  failedStudentIds: [],
};

// the async thunk to fetch the manifest data from the API
export const fetchManifestData = createAsyncThunk<
  { response: Manifest[]; grade: number },
  {
    assignmentType: string;
    schoolId: string;
    districtId: string;
    grade: number;
  }
>(
  'scheduleAssessment/fetchManifestData',
  async (
    { assignmentType, schoolId, districtId, grade },
    { rejectWithValue }
  ) => {
    try {
      const response = (await fetchManifest({
        assignmentType,
        schoolId,
        districtId,
      })) as ManifestResponse;
      return { response: response.generateManifest.activities, grade };
    } catch (error: any) {
      console.error('Error fetching manifest data', error);
      return rejectWithValue({ message: error.message, grade });
    }
  }
);

// the async thunk to fetch the manifest data from the API
export const bulkScheduleAssessments = createAsyncThunk<
  ManifestResponse,
  ScheduleAssessmentPayload
>(
  'scheduleAssessment/bulkScheduleAssessments',
  async (payload, { rejectWithValue }) => {
    try {
      const response = (await batchAddAssignment(payload)) as ManifestResponse;
      return response;
    } catch (error: any) {
      console.error('Error scheduling assessments', error);
      return rejectWithValue({
        message: error.message,
        studentIds: payload.entityIdList,
      });
    }
  }
);

const scheduleAssessmentSlice = createSlice({
  name: 'scheduleAssessment',
  initialState,
  reducers: {
    resetScheduleAssessmentState(state) {
      state = { ...initialState };
    },
    clearFailedStudentIds(state) {
      state.failedStudentIds = [];
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(
        fetchManifestData.fulfilled,
        (
          state,
          action: PayloadAction<{
            response: Manifest[];
            grade: number;
          }>
        ) => {
          state.manifestActivities[action.payload.grade] =
            action.payload.response;
        }
      )
      .addCase(fetchManifestData.pending, state => {
        state.error = null;
      })
      .addCase(fetchManifestData.rejected, (state, action) => {
        state.manifestActivities[(action.payload as { grade: number }).grade] =
          [];
        state.error = (action.payload as { message: string }).message;
      })
      .addCase(bulkScheduleAssessments.fulfilled, state => {
        state.loading = state.loading - 1;
      })
      .addCase(bulkScheduleAssessments.pending, state => {
        state.loading = state.loading + 1;
      })
      .addCase(bulkScheduleAssessments.rejected, (state, action) => {
        state.loading = state.loading - 1;
        state.failedStudentIds.push(
          ...(action.payload as { studentIds: string[] }).studentIds
        );
        state.error = (action.payload as { message: string }).message;
      });
  },
});

export const { resetScheduleAssessmentState, clearFailedStudentIds } = scheduleAssessmentSlice.actions;
export const scheduleAssessmentData = (state: any) => state.scheduleAssessment;
export default scheduleAssessmentSlice.reducer;
